import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import { flowStyles } from "../styles/flow-styles";
import {
  getActiveIob,
  getRecentGlucose,
  loadUserSettings,
  saveFocusedEntry,
  saveManualGlucoseReading,
  REMEMBER_GL_WINDOW_MINUTES,
} from "../utils/entry-flow";
import { calculateCorrectionDose } from "../config/dafne/insulin";
import { BG_THRESHOLDS } from "../config/dafne/thresholds";
import { DEFAULT_SETTINGS } from "../types";
import type { UserSettings } from "../types";

/** DAFNE standard target glucose used for correction suggestions (mmol/L). */
const TARGET_GL = 6.0;

/**
 * Focused flow: log a quick-acting correction or standalone quick dose.
 * Suggests a correction from the user's correction factor and active IOB, using
 * a remembered recent glucose where available. See docs/add-task-flows.md.
 */
@customElement("add-quick-page")
export class AddQuickPage extends LitElement {
  @state() private gl = "";
  @state() private glFromMemory = false;
  @state() private recentAgeMinutes: number | null = null;
  @state() private dose = "";
  @state() private correctionIob = 0;
  @state() private isSaving = false;
  @state() private showSaved = false;
  @state() private settings: UserSettings = { ...DEFAULT_SETTINGS };

  static styles = [
    flowStyles,
    css`
      .suggest-btn {
        margin-top: 0.5rem;
      }
      .memory-note {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.8rem;
        color: #0d6efd;
        background: #e7f1ff;
        padding: 0.5rem 0.75rem;
        border-radius: 0.5rem;
        margin-bottom: 0.75rem;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    void this._load();
  }

  private async _load() {
    this.settings = await loadUserSettings();
    const iob = await getActiveIob();
    this.correctionIob = iob.correction;

    const recent = await getRecentGlucose(REMEMBER_GL_WINDOW_MINUTES);
    if (recent) {
      this.gl = recent.glucose.toFixed(1);
      this.glFromMemory = true;
      this.recentAgeMinutes = recent.ageMinutes;
    }
  }

  private _onGlInput(value: string) {
    this.gl = value;
    this.glFromMemory = false;
    this.recentAgeMinutes = null;
  }

  private get _suggestedCorrection(): number | null {
    const gl = parseFloat(this.gl);
    const cf = this.settings.correctionFactor;
    if (isNaN(gl) || !cf) return null;
    if (gl < BG_THRESHOLDS.HYPO) return null; // never correct a hypo
    const net = calculateCorrectionDose(gl, TARGET_GL, cf, this.correctionIob);
    return net;
  }

  private _applySuggestion(units: number) {
    const inc = this.settings.doseIncrement || 0.5;
    this.dose = (Math.round(units / inc) * inc).toString();
  }

  private _handleBack() {
    Router.go("/");
  }

  private async _handleSave() {
    const dose = parseFloat(this.dose);
    if (isNaN(dose) || dose <= 0 || this.isSaving) return;

    this.isSaving = true;
    try {
      const when = new Date();
      const gl = parseFloat(this.gl);
      const hasFreshGl = !this.glFromMemory && !isNaN(gl) && gl > 0;

      const fields: Parameters<typeof saveFocusedEntry>[0] = {
        qaCorrection: dose,
      };
      if (hasFreshGl) {
        fields.bloodGlucose = gl;
        if (this.settings.correctionFactor) {
          fields.correctionFactorUsed = this.settings.correctionFactor;
        }
        await saveManualGlucoseReading(gl, when);
      }

      await saveFocusedEntry(fields, this.settings, when);
      this.showSaved = true;
      setTimeout(() => Router.go("/"), 500);
    } catch (err) {
      console.error("Failed to save quick dose:", err);
      this.isSaving = false;
    }
  }

  private _renderGuidance() {
    const gl = parseFloat(this.gl);
    if (isNaN(gl)) return nothing;

    if (gl < BG_THRESHOLDS.HYPO) {
      return html`
        <wa-callout variant="danger" open>
          <wa-icon name="triangle-exclamation" slot="icon"></wa-icon>
          <strong>Glucose is low (${gl.toFixed(1)} mmol/L)</strong>
          <p style="margin: 0.5rem 0 0 0;">
            A correction is not recommended while low, based on OZDAFNE
            guidelines. Treat the hypo first.
          </p>
        </wa-callout>
      `;
    }

    const cf = this.settings.correctionFactor;
    if (!cf) {
      return html`
        <wa-callout variant="neutral" open>
          <wa-icon name="circle-info" slot="icon"></wa-icon>
          Set your correction factor in Settings to see a suggested correction.
        </wa-callout>
      `;
    }

    const suggested = this._suggestedCorrection;
    if (suggested === null) return nothing;

    if (suggested <= 0.25) {
      return html`
        <wa-callout variant="success" open>
          <wa-icon name="circle-check" slot="icon"></wa-icon>
          No correction
          suggested${this.correctionIob > 0
            ? html` - you have ${this.correctionIob.toFixed(1)}u active
              correction insulin`
            : ""}.
        </wa-callout>
      `;
    }

    return html`
      <wa-callout variant="warning" open>
        <wa-icon name="syringe" slot="icon"></wa-icon>
        <strong>Suggested correction: ${suggested.toFixed(1)}u</strong>
        ${this.correctionIob > 0
          ? html`<p style="margin: 0.25rem 0 0 0; font-size: 0.8rem;">
              After ${this.correctionIob.toFixed(1)}u active correction insulin,
              using 1u per ${cf} mmol/L toward ${TARGET_GL} mmol/L.
            </p>`
          : html`<p style="margin: 0.25rem 0 0 0; font-size: 0.8rem;">
              Using 1u per ${cf} mmol/L toward ${TARGET_GL} mmol/L.
            </p>`}
        <wa-button
          class="suggest-btn"
          size="small"
          variant="brand"
          @click=${() => this._applySuggestion(suggested)}
        >
          Use ${suggested.toFixed(1)}u
        </wa-button>
      </wa-callout>
    `;
  }

  render() {
    const dose = parseFloat(this.dose);
    const canSave = !isNaN(dose) && dose > 0 && !this.isSaving;

    return html`
      <div class="page-container">
        ${this.showSaved
          ? html`
              <wa-callout variant="success" open class="success-banner">
                <wa-icon name="circle-check" slot="icon"></wa-icon>
                Quick dose saved!
              </wa-callout>
            `
          : nothing}

        <div class="page-header">
          <button class="back-btn" @click=${this._handleBack} aria-label="Back">
            <wa-icon name="arrow-left"></wa-icon>
          </button>
          <h1>Quick / correction</h1>
          ${this.correctionIob > 0
            ? html`<span class="header-badge"
                >IOB ${this.correctionIob.toFixed(1)}u</span
              >`
            : nothing}
        </div>

        <div class="form-section">
          ${this.glFromMemory && this.recentAgeMinutes !== null
            ? html`
                <div class="memory-note">
                  <wa-icon name="clock-rotate-left"></wa-icon>
                  Using ${parseFloat(this.gl).toFixed(1)} mmol/L from
                  ${this.recentAgeMinutes} min ago - edit to change.
                </div>
              `
            : nothing}

          <div class="input-group">
            <div class="input-label">
              <wa-icon name="droplet"></wa-icon>
              Current glucose (optional)
            </div>
            <wa-input
              class="flow-input"
              type="number"
              inputmode="decimal"
              min="1"
              max="33"
              step="0.1"
              placeholder="GL"
              .value=${this.gl}
              @input=${(e: Event) =>
                this._onGlInput((e.target as HTMLInputElement).value)}
            ></wa-input>
            <div class="unit-hint">mmol/L</div>
          </div>

          ${this._renderGuidance()}

          <div class="input-group" style="margin-top: 1rem;">
            <div class="input-label">
              <wa-icon name="syringe"></wa-icon>
              Quick insulin to log
            </div>
            <wa-input
              class="flow-input"
              type="number"
              inputmode="decimal"
              min="0"
              max="100"
              step="0.5"
              placeholder="units"
              .value=${this.dose}
              @input=${(e: Event) =>
                (this.dose = (e.target as HTMLInputElement).value)}
            ></wa-input>
            <div class="unit-hint">units</div>
          </div>

          <div class="actions">
            <wa-button
              class="save-btn"
              variant="brand"
              size="large"
              ?disabled=${!canSave}
              ?loading=${this.isSaving}
              @click=${this._handleSave}
            >
              ${this.isSaving ? "Saving..." : "Save quick dose"}
            </wa-button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "add-quick-page": AddQuickPage;
  }
}
